import React from 'react';
import { Handle, Position } from 'reactflow';
import { GripVertical } from 'lucide-react';

export const TableNode = ({ data, selected }) => {
  const [hoveredColumn, setHoveredColumn] = React.useState(null);
  
  // Check if column has active relationships
  const hasActiveRelationship = (columnId) => {
    if (!data.activeRelationships) return false;
    return data.activeRelationships.includes(columnId);
  };
  
  return (
    <div
      data-testid={`table-node-${data.id}`}
      className={`bg-card border rounded-md shadow-sm min-w-[220px] transition-all duration-200 relative ${
        selected ? 'border-primary ring-2 ring-primary/20 shadow-lg' : 'border-border'
      }`}
    >
      {/* Header */}
      <div className="border-b border-border p-2 bg-accent/30 rounded-t-md flex items-center space-x-2">
        <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" strokeWidth={1.5} />
        <span className="font-bold text-sm" style={{fontFamily: 'JetBrains Mono'}}>
          {data.name || 'Untitled Table'}
        </span>
      </div>
      
      {/* Columns with handles */}
      <div className="p-2 space-y-1">
        {data.columns && data.columns.length > 0 ? (
          data.columns.map((column, index) => {
            const hasRelation = hasActiveRelationship(column.id);
            const isHovered = hoveredColumn === column.id;
            const showHandles = hasRelation || isHovered;
            
            return (
              <div
                key={column.id}
                data-testid={`column-${column.id}`}
                className="relative flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-accent/50 transition-colors duration-200 cursor-pointer group"
                onClick={() => data.onColumnClick?.(column)}
                onMouseEnter={() => setHoveredColumn(column.id)}
                onMouseLeave={() => setHoveredColumn(null)}
              >
                {/* LEFT HANDLE */}
                <Handle
                  type="source"
                  position={Position.Left}
                  id={`${column.id}-left`}
                  className={`!w-3 !h-3 !border-2 !border-white transition-all duration-200 ${
                    hasRelation 
                      ? '!bg-blue-500 hover:!bg-blue-600' 
                      : '!bg-gray-400 group-hover:!bg-blue-500'
                  } hover:!scale-125`}
                  style={{ 
                    left: -12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: showHandles ? 1 : 0
                  }}
                />
                <Handle
                  type="target"
                  position={Position.Left}
                  id={`${column.id}-left`}
                  className={`!w-3 !h-3 !border-2 !border-white transition-all duration-200 ${
                    hasRelation 
                      ? '!bg-blue-500 hover:!bg-blue-600' 
                      : '!bg-gray-400 group-hover:!bg-blue-500'
                  } hover:!scale-125`}
                  style={{ 
                    left: -12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: showHandles ? 1 : 0
                  }}
                />
                
                {/* Column content */}
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  {column.is_primary_key && (
                    <span className="text-primary font-bold" title="Primary Key">🔑</span>
                  )}
                  <span className="font-medium truncate" style={{fontFamily: 'JetBrains Mono'}}>
                    {column.name}
                  </span>
                </div>
                <span className="text-muted-foreground text-[10px] ml-2" style={{fontFamily: 'JetBrains Mono'}}>
                  {column.data_type}{column.length ? `(${column.length})` : ''}
                </span>
                
                {/* RIGHT HANDLE */}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`${column.id}-right`}
                  className={`!w-3 !h-3 !border-2 !border-white transition-all duration-200 ${
                    hasRelation 
                      ? '!bg-blue-500 hover:!bg-blue-600' 
                      : '!bg-gray-400 group-hover:!bg-blue-500'
                  } hover:!scale-125`}
                  style={{ 
                    right: -12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: showHandles ? 1 : 0
                  }}
                />
                <Handle
                  type="target"
                  position={Position.Right}
                  id={`${column.id}-right`}
                  className={`!w-3 !h-3 !border-2 !border-white transition-all duration-200 ${
                    hasRelation 
                      ? '!bg-blue-500 hover:!bg-blue-600' 
                      : '!bg-gray-400 group-hover:!bg-blue-500'
                  } hover:!scale-125`}
                  style={{ 
                    right: -12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: showHandles ? 1 : 0
                  }}
                />
              </div>
            );
          })
        ) : (
          <div className="text-xs text-muted-foreground text-center py-2">No columns</div>
        )}
      </div>
    </div>
  );
};
